/**
 * Backend API historical data provider for the backtest engine
 * Uses server-side API credentials for better security
 */
import axios from 'axios';
import { HistoricalDataProvider, HistoricalPrice } from '../types.js';

// Added type definitions for the API response
interface BacktestApiTrade {
  symbol: string;
  entryDate: string;
  entryPrice: number;
  exitDate: string;
  exitPrice: number;
  // Add other trade properties if known/needed
}

interface BacktestApiEquityPoint {
  date: string;
  value: number;
  // Add other equity point properties if known/needed
}

interface BacktestApiResponse {
  equityCurve: BacktestApiEquityPoint[];
  trades: BacktestApiTrade[];
  fullTimeSeries?: HistoricalPrice[]; // Optional full time series data
  // Add other potential response properties if known
}

// Added for the direct historical data API call
interface DirectHistoricalDataResponse {
  data: HistoricalPrice[];
  error?: string;
  details?: string;
}

export class BackendHistoricalDataProvider implements HistoricalDataProvider {
  private provider: string;
  private isPaper: boolean;
  private baseUrl: string;
  
  constructor(provider: string, isPaper: boolean = true) {
    // Default to Alpaca if provider is unspecified or invalid
    this.provider = provider && ['alpaca', 'polygon'].includes(provider.toLowerCase()) 
      ? provider.toLowerCase() 
      : 'alpaca';
    this.isPaper = isPaper;
    this.baseUrl = '/api/backtest';
    
    console.log(`BackendHistoricalDataProvider initialized with provider: ${this.provider}`);
  }
  
  /**
   * Fetch historical data from backend API which has secure access to Alpaca/Polygon
   */
  async fetchHistoricalData(
    symbols: string[], 
    startDate: string, 
    endDate: string, 
    timeframe: string
  ): Promise<HistoricalPrice[]> {
    console.log(`Attempting to fetch historical data for ${symbols.length} symbols from ${startDate} to ${endDate} using ${this.provider}`);
    
    // Calculate last market close (approximate implementation)
    const getLastMarketCloseDate = (): string => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // If yesterday was a weekend, adjust further back
      const dayOfWeek = yesterday.getDay(); // 0 = Sunday, 6 = Saturday
      
      if (dayOfWeek === 0) { // Sunday -> use Friday
        yesterday.setDate(yesterday.getDate() - 2);
      } else if (dayOfWeek === 6) { // Saturday -> use Friday
        yesterday.setDate(yesterday.getDate() - 1);
      }
      
      // Format as YYYY-MM-DD
      return yesterday.toISOString().split('T')[0];
    };

    // Check if endDate is today or in the future and adjust to last market close
    const today = new Date().toISOString().split('T')[0];
    const originalEndDateObj = new Date(endDate);
    const todayObj = new Date(today);
    
    if (originalEndDateObj >= todayObj) {
      const lastMarketClose = getLastMarketCloseDate();
      console.log(`Adjusting end date ${endDate} to last market close: ${lastMarketClose}`);
      endDate = lastMarketClose;
    }
    
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate); // Use the potentially adjusted endDate
    
    // Check if date range spans into the future - use adjusted dates
    const isFutureBacktest = endDateObj > todayObj;
    
    // Ensure we don't request more than 1 year of data (to avoid API limits)
    const oneYearAgo = new Date(todayObj);
    oneYearAgo.setFullYear(todayObj.getFullYear() - 1);
    
    let originalStartDate = startDate;
    let originalEndDate = endDate;
    
    if (startDateObj < oneYearAgo) {
      const adjustedStartDate = oneYearAgo.toISOString().split('T')[0];
      console.log(`Adjusting start date from ${startDate} to one year ago (${adjustedStartDate}) to respect API limits`);
      startDate = adjustedStartDate;
    }

    // Try all three API endpoints in sequence
    let historicalData: HistoricalPrice[] = [];
    let apiErrors: string[] = [];
    
    // Common HTTP headers for all requests
    const commonHeaders = {
      'Content-Type': 'application/json',
      'X-Preferred-Provider': this.provider,
      'Accept': 'application/json'
    };
    
    // Common request payload for all endpoints
    const requestPayload = {
      symbols,
      startDate,
      endDate,
      timeframe,
      provider: this.provider,
      isPaper: this.isPaper,
      fillGaps: true
    };
    
    // Define interfaces for API responses
    interface ApiResponse {
      data?: {
        data?: HistoricalPrice[];
        error?: string;
        details?: string;
      };
      status: number;
      statusText: string;
    }
    
    // Create a client that will better handle CORS and other potential issues
    const createClient = () => {
      // Use window.location.origin for development, but use the baseUrl for production
      const baseURL = window.location.hostname === 'localhost' ? 
        window.location.origin : 
        'https://www.stewietrader.com';

      return axios.create({
        baseURL, // Use determined baseURL instead of always using window.location.origin
        timeout: 60000,  // 60 second timeout
        headers: commonHeaders,
        validateStatus: (status) => {
          // Consider only 5xx as errors to better handle different API responses
          return status < 500;
        }
      });
    };
    
    // 0. First try to use local server data endpoint
    try {
      console.log(`Attempting to fetch directly from local /api/backtest/historical-data`);
      
      // Create a dedicated client with appropriate settings for this request
      const client = createClient();
      
      // Determine the HTTP method based on the hostname - use GET for production site
      const isProduction = window.location.hostname !== 'localhost';
      const method = isProduction ? 'get' : 'post';
      
      // For GET requests, add parameters to the URL
      let response: ApiResponse;
      if (method === 'get') {
        // Convert the payload to query parameters
        const params = new URLSearchParams();
        params.append('symbols', JSON.stringify(symbols));
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('timeframe', timeframe);
        params.append('provider', this.provider);
        params.append('isPaper', String(this.isPaper));
        params.append('fillGaps', 'true');
        
        response = await client.get(`/api/backtest/historical-data?${params.toString()}`) as ApiResponse;
      } else {
        response = await client.post('/api/backtest/historical-data', requestPayload) as ApiResponse;
      }
      
      // Handle specific status codes that might help troubleshoot
      if (response.status === 405) {
        console.warn('Received Method Not Allowed (405) - endpoint might not support POST, trying next endpoint');
        apiErrors.push('Method Not Allowed (405) for historical-data endpoint');
      } else if (response.status >= 400 && response.status < 500) {
        console.warn(`Client error (${response.status}) for historical-data endpoint: ${response.statusText}`);
        apiErrors.push(`Client error (${response.status}) for historical-data endpoint`);
      } else if (response.status >= 200 && response.status < 300 && response.data?.data?.length > 0) {
        console.log(`Successfully fetched ${response.data.data.length} points from local historical-data endpoint.`);
        
        // Validate data completeness
        const isDataContinuous = this.validateDataContinuity(response.data.data, startDate, endDate);
        if (isDataContinuous) {
          console.log('Data validation passed: continuous data available for backtest');
          
          // If we need future data, extend the dataset
          if (isFutureBacktest) {
            console.log(`Extending data into the future for predictive backtest from ${endDate} to ${originalEndDate}`);
            const futureData = this.generateFutureData(response.data.data, endDate, originalEndDate, timeframe);
            const combinedData = [...response.data.data, ...futureData];
            return combinedData;
          }
          
          return response.data.data;
        } else {
          console.warn('Data validation failed: gaps detected in historical data');
          historicalData = response.data.data; // Still use the data, but continue to try other methods
        }
      }
    } catch (error: any) {
      const errorMsg = `Failed to fetch from local historical-data endpoint: ${error.message}`;
      console.warn(errorMsg + `. Trying next endpoint.`);
      apiErrors.push(errorMsg);
    }
    
    // 1. Try the comprehensive data endpoint
    try {
      console.log(`Attempting to fetch from /api/backtest/comprehensive-data`);
      
      // Create a dedicated client with appropriate settings for this request
      const client = createClient();
      
      // Determine the HTTP method based on the hostname - use GET for production site
      const isProduction = window.location.hostname !== 'localhost';
      const method = isProduction ? 'get' : 'post';
      
      // For GET requests, add parameters to the URL
      let response: ApiResponse;
      if (method === 'get') {
        // Convert the payload to query parameters
        const params = new URLSearchParams();
        params.append('symbols', JSON.stringify(symbols));
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('timeframe', timeframe);
        params.append('provider', this.provider);
        params.append('isPaper', String(this.isPaper));
        params.append('fillGaps', 'true');
        
        response = await client.get(`/api/backtest/comprehensive-data?${params.toString()}`) as ApiResponse;
      } else {
        response = await client.post('/api/backtest/comprehensive-data', requestPayload) as ApiResponse;
      }
      
      // Handle specific status codes that might help troubleshoot
      if (response.status === 405) {
        console.warn('Received Method Not Allowed (405) - endpoint might not support POST, trying next endpoint');
        apiErrors.push('Method Not Allowed (405) for comprehensive-data endpoint');
      } else if (response.status >= 400 && response.status < 500) {
        console.warn(`Client error (${response.status}) for comprehensive-data endpoint: ${response.statusText}`);
        apiErrors.push(`Client error (${response.status}) for comprehensive-data endpoint`);
      } else if (response.status >= 200 && response.status < 300 && response.data?.data?.length > 0) {
        console.log(`Successfully fetched ${response.data.data.length} points from comprehensive-data endpoint.`);
        
        // Validate data completeness
        const isDataContinuous = this.validateDataContinuity(response.data.data, startDate, endDate);
        if (isDataContinuous) {
          console.log('Data validation passed: continuous data available for backtest');
          
          // If we need future data, extend the dataset
          if (isFutureBacktest) {
            console.log(`Extending data into the future for predictive backtest from ${endDate} to ${originalEndDate}`);
            const futureData = this.generateFutureData(response.data.data, endDate, originalEndDate, timeframe);
            const combinedData = [...response.data.data, ...futureData];
            return combinedData;
          }
          
          return response.data.data;
        } else {
          console.warn('Data validation failed: gaps detected in historical data');
          historicalData = response.data.data; // Still use the data, but continue to try other methods
        }
      }
    } catch (error: any) {
      const errorMsg = `Failed to fetch from comprehensive-data endpoint: ${error.message}`;
      console.warn(errorMsg + `. Trying next endpoint.`);
      apiErrors.push(errorMsg);
    }
    
    // 2. Fallback to existing workaround: using /api/backtest/run
    try {
      console.log(`Fetching historical data using existing backtest API endpoint (workaround) with provider: ${this.provider}`);
      
      // Build a minimal backtest request that should return data we can use
      const backtestRequest = {
        strategy_id: `historical_data_${Date.now()}`,
        symbols: symbols.map(s => ({ ticker: s, direction: 'auto', weight: 1 })),
        settings: {
          mode: 'data_retrieve_only',  // Signal this is just for data retrieval
          startDate,
          endDate,
          timeframe,
          provider: this.provider,  // Use the provider that was set in the constructor
          initialCapital: '10000',
          includeCommission: false,
          slippageModel: 'none',
          dataUpdateInterval: '1d',
          fetchFullData: true  // Request full data series not just trade points
        }
      };
      
      // Log the request details for debugging
      console.log(`Sending backtest API request with provider: ${this.provider}`);
      
      // Create a dedicated client with appropriate settings for this request
      const client = createClient();
      
      // Determine the HTTP method based on the hostname - use GET for production site
      const isProduction = window.location.hostname !== 'localhost';
      const method = isProduction ? 'get' : 'post';
      
      // For GET requests, add parameters to the URL
      let response: ApiResponse;
      if (method === 'get') {
        // Convert the payload to query parameters
        const params = new URLSearchParams();
        params.append('strategy_id', backtestRequest.strategy_id);
        params.append('symbols', JSON.stringify(backtestRequest.symbols));
        params.append('settings', JSON.stringify(backtestRequest.settings));
        
        response = await client.get(`/api/backtest/run?${params.toString()}`) as ApiResponse;
      } else {
        response = await client.post('/api/backtest/run', backtestRequest) as ApiResponse;
      }
      
      // Handle specific status codes that might help troubleshoot
      if (response.status === 405) {
        console.warn('Received Method Not Allowed (405) - endpoint might not support POST, trying fallback to mock data');
        apiErrors.push('Method Not Allowed (405) for backtest/run endpoint');
      } else if (response.status >= 400 && response.status < 500) {
        console.warn(`Client error (${response.status}) for backtest/run endpoint: ${response.statusText}`);
        apiErrors.push(`Client error (${response.status}) for backtest/run endpoint`);
      } else if (response.status >= 200 && response.status < 300) {
        // Type cast response.data for safety, although axios.post<T> should type it
        const responseData = response.data as BacktestApiResponse;
  
        if (responseData && responseData.equityCurve && responseData.trades) {
          console.log(`Successfully received data from backtest API`);
          
          // Extract dates from equity curve
          const dates = responseData.equityCurve.map((point) => point.date);
          
          // Get unique symbols from trades
          const uniqueSymbols = [...new Set(responseData.trades.map((trade) => trade.symbol))];
          
          // For missing symbols, add the ones from our original request
          symbols.forEach(s => {
            if (!uniqueSymbols.includes(s)) {
              uniqueSymbols.push(s);
            }
          });
          
          // Generate a more comprehensive dataset from the trades and other data
          let apiHistoricalData: HistoricalPrice[] = [];
          
          // First try to extract a full time series if available in the response
          if (responseData.fullTimeSeries) {
            apiHistoricalData = responseData.fullTimeSeries;
            console.log(`Using full time series data from API response (${apiHistoricalData.length} points)`);
          } else {
            // Process trade data to create data points
            console.log('Creating comprehensive data from trade points');
            const tradeDataPoints = this.generateComprehensiveDataFromTrades(
              responseData.trades, 
              startDate, 
              endDate, 
              timeframe
            );
            apiHistoricalData = tradeDataPoints;
          }
          
          // Check if data is sufficient or needs supplementing
          if (this.isDataSufficient(apiHistoricalData, symbols, startDate, endDate)) {
            console.log(`Data is sufficient for backtest with ${apiHistoricalData.length} points`);
            
            // If we need future data, extend the dataset
            if (isFutureBacktest) {
              console.log(`Extending data into the future for predictive backtest from ${endDate} to ${originalEndDate}`);
              const futureData = this.generateFutureData(apiHistoricalData, endDate, originalEndDate, timeframe);
              const combinedData = [...apiHistoricalData, ...futureData];
              return combinedData;
            }
            
            return apiHistoricalData;
          }
          
          // If we have no data yet or the new data is better quality, use it
          if (historicalData.length === 0 || apiHistoricalData.length > historicalData.length) {
            historicalData = apiHistoricalData;
          }
        }
      }
    } catch (error: any) {
      const errorMsg = `Error fetching data from API: ${error.message}`;
      console.warn(errorMsg);
      apiErrors.push(errorMsg);
    }

    // If we have some data from any of the API methods, use it with synthetic data to fill gaps
    if (historicalData.length > 0) {
      console.log(`Using partial data (${historicalData.length} points) from API with synthetic data to fill gaps`);
      
      // Fill gaps in the existing data
      const mockData = this.generateContinuousHistoricalData(symbols, startDate, endDate, timeframe, historicalData);
      const mergedData = this.mergeRealAndMockData(historicalData, mockData);
      
      // If we need future data, extend the dataset
      if (isFutureBacktest) {
        console.log(`Extending data into the future for predictive backtest from ${endDate} to ${originalEndDate}`);
        const futureData = this.generateFutureData(mergedData, endDate, originalEndDate, timeframe);
        const combinedData = [...mergedData, ...futureData];
        return combinedData;
      }
      
      return mergedData;
    }

    // All API methods failed, fall back to complete mock data generation
    console.log(`Falling back to mock historical data generation (after all attempts).`);
    
    // Add errors to console for debugging
    console.error(`API Errors: ${apiErrors.join('; ')}`);
    
    const mockData = this.generateMockHistoricalData(symbols, startDate, endDate, timeframe);
    
    // If we need future data, extend the dataset (though in this case, it's all mock data anyway)
    if (isFutureBacktest) {
      console.log(`Using mock data for future backtest from ${startDate} to ${originalEndDate}`);
      const futureData = this.generateMockHistoricalData(symbols, startDate, originalEndDate, timeframe);
      return futureData;
    }
    
    return mockData;
  }
  
  /**
   * Generate continuous mock historical data as a fallback when API fails
   */
  private generateContinuousHistoricalData(
    symbols: string[],
    startDate: string,
    endDate: string,
    timeframe: string,
    existingData: HistoricalPrice[] = []
  ): HistoricalPrice[] {
    // Use existing generateMockHistoricalData but make sure it's continuous
    const baseData = this.generateMockHistoricalData(symbols, startDate, endDate, timeframe);
    
    // Ensure data is continuous by filling any gaps
    return this.ensureDataContinuity(baseData, startDate, endDate, timeframe);
  }
  
  /**
   * Generate comprehensive data from trade points by interpolating between them
   */
  private generateComprehensiveDataFromTrades(
    trades: BacktestApiTrade[],
    startDate: string,
    endDate: string,
    timeframe: string
  ): HistoricalPrice[] {
    const dataPoints: HistoricalPrice[] = [];
    
    // First extract all known data points from trades
    trades.forEach((trade) => {
      // Convert entry data to a price point
      dataPoints.push({
        symbol: trade.symbol,
        date: trade.entryDate,
        open: trade.entryPrice * 0.99,
        high: trade.entryPrice * 1.01,
        low: trade.entryPrice * 0.98,
        close: trade.entryPrice,
        volume: Math.floor(Math.random() * 1000000) + 500000
      });
      
      // Convert exit data to a price point
      dataPoints.push({
        symbol: trade.symbol,
        date: trade.exitDate,
        open: trade.exitPrice * 0.99,
        high: trade.exitPrice * 1.01,
        low: trade.exitPrice * 0.98,
        close: trade.exitPrice,
        volume: Math.floor(Math.random() * 1000000) + 500000
      });
    });
    
    // Group data points by symbol
    const symbolData: Record<string, HistoricalPrice[]> = {};
    dataPoints.forEach(point => {
      if (!symbolData[point.symbol]) {
        symbolData[point.symbol] = [];
      }
      symbolData[point.symbol].push(point);
    });
    
    // For each symbol, interpolate between known data points
    const interpolatedData: HistoricalPrice[] = [];
    
    Object.entries(symbolData).forEach(([symbol, points]) => {
      // Sort points by date
      points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      // Generate all days in the range
      const allDates = this.generateDateRange(startDate, endDate, timeframe);
      
      // For each date, find or interpolate a data point
      allDates.forEach(date => {
        const dateStr = date.toISOString().split('T')[0];
        
        // Check if we have actual data for this date
        const existingPoint = points.find(p => p.date.startsWith(dateStr));
        
        if (existingPoint) {
          // Use existing data
          interpolatedData.push(existingPoint);
        } else {
          // Find closest points before and after
          const before = [...points].reverse().find(p => new Date(p.date) < date);
          const after = points.find(p => new Date(p.date) > date);
          
          if (before && after) {
            // Interpolate between points
            const interpolated = this.interpolateDataPoint(before, after, date);
            interpolatedData.push(interpolated);
          } else if (before) {
            // Extrapolate forward slightly
            const modified = {...before};
            modified.date = dateStr;
            // Slight random variation
            const variation = (Math.random() * 0.01 - 0.005); // ±0.5%
            modified.close *= (1 + variation);
            modified.open *= (1 + variation);
            modified.high *= (1 + variation);
            modified.low *= (1 + variation);
            interpolatedData.push(modified);
          } else if (after) {
            // Extrapolate backward slightly
            const modified = {...after};
            modified.date = dateStr;
            // Slight random variation
            const variation = (Math.random() * 0.01 - 0.005); // ±0.5%
            modified.close *= (1 + variation);
            modified.open *= (1 + variation);
            modified.high *= (1 + variation);
            modified.low *= (1 + variation);
            interpolatedData.push(modified);
          }
          // Skip dates with no reference points
        }
      });
    });
    
    return interpolatedData;
  }
  
  /**
   * Interpolate a data point between two known points
   */
  private interpolateDataPoint(
    before: HistoricalPrice,
    after: HistoricalPrice,
    targetDate: Date
  ): HistoricalPrice {
    const beforeDate = new Date(before.date);
    const afterDate = new Date(after.date);
    
    // Calculate weight based on time position
    const totalInterval = afterDate.getTime() - beforeDate.getTime();
    const targetInterval = targetDate.getTime() - beforeDate.getTime();
    const weight = totalInterval === 0 ? 0.5 : targetInterval / totalInterval;
    
    // Linear interpolation function
    const lerp = (a: number, b: number, w: number) => a + (b - a) * w;
    
    // Interpolate OHLCV values
    return {
      symbol: before.symbol,
      date: targetDate.toISOString().split('T')[0],
      open: lerp(before.open, after.open, weight),
      high: lerp(before.high, after.high, weight),
      low: lerp(before.low, after.low, weight),
      close: lerp(before.close, after.close, weight),
      volume: Math.floor(lerp(before.volume, after.volume, weight))
    };
  }
  
  /**
   * Generate a continuous date range based on timeframe
   */
  private generateDateRange(
    startDate: string,
    endDate: string,
    timeframe: string
  ): Date[] {
    const dates: Date[] = [];
    let current = new Date(startDate);
    const end = new Date(endDate);
    
    while (current <= end) {
      // Skip weekends for daily data
      if (timeframe === 'day' || timeframe === 'week') {
        const day = current.getDay();
        if (day !== 0 && day !== 6) {
          dates.push(new Date(current));
        }
      } else {
        dates.push(new Date(current));
      }
      
      // Increment based on timeframe
      switch (timeframe) {
        case 'minute':
          current = new Date(current.getTime() + 60 * 1000);
          break;
        case '5minute':
          current = new Date(current.getTime() + 5 * 60 * 1000);
          break;
        case '15minute':
          current = new Date(current.getTime() + 15 * 60 * 1000);
          break;
        case 'hour':
          current = new Date(current.getTime() + 60 * 60 * 1000);
          break;
        case 'day':
          current = new Date(current.setDate(current.getDate() + 1));
          break;
        case 'week':
          current = new Date(current.setDate(current.getDate() + 7));
          break;
        default:
          current = new Date(current.setDate(current.getDate() + 1));
      }
    }
    
    return dates;
  }
  
  /**
   * Ensure data has continuity by filling gaps
   */
  private ensureDataContinuity(
    data: HistoricalPrice[],
    startDate: string,
    endDate: string,
    timeframe: string
  ): HistoricalPrice[] {
    // Get all unique symbols
    const symbols = [...new Set(data.map(d => d.symbol))];
    
    // Generate all dates in the range
    const allDates = this.generateDateRange(startDate, endDate, timeframe);
    
    // Check and fill gaps for each symbol
    const continuousData: HistoricalPrice[] = [];
    
    symbols.forEach(symbol => {
      const symbolData = data.filter(d => d.symbol === symbol);
      
      // Create a map of existing dates for quick lookup
      const dateMap = new Map<string, HistoricalPrice>();
      symbolData.forEach(point => {
        const dateKey = point.date.split('T')[0];
        dateMap.set(dateKey, point);
      });
      
      // For each date in the range, ensure we have data
      allDates.forEach(date => {
        const dateKey = date.toISOString().split('T')[0];
        
        if (dateMap.has(dateKey)) {
          // Use existing data
          continuousData.push(dateMap.get(dateKey)!);
        } else {
          // Fill gap with synthesized data
          const filledPoint = this.synthesizeDataPoint(symbol, date, symbolData);
          continuousData.push(filledPoint);
        }
      });
    });
    
    return continuousData;
  }
  
  /**
   * Synthesize a data point for a gap using nearby points
   */
  private synthesizeDataPoint(
    symbol: string,
    date: Date,
    existingData: HistoricalPrice[]
  ): HistoricalPrice {
    // Sort data by date
    const sortedData = [...existingData].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    // Find nearest point before and after
    const before = sortedData.filter(d => new Date(d.date) < date).pop();
    const after = sortedData.find(d => new Date(d.date) > date);
    
    if (before && after) {
      // Interpolate between points
      return this.interpolateDataPoint(before, after, date);
    } else if (before) {
      // Use the last known point with slight variation
      return {
        symbol,
        date: date.toISOString().split('T')[0],
        open: before.open * (1 + (Math.random() * 0.01 - 0.005)),
        high: before.high * (1 + (Math.random() * 0.01)),
        low: before.low * (1 - (Math.random() * 0.01)),
        close: before.close * (1 + (Math.random() * 0.01 - 0.005)),
        volume: Math.floor(before.volume * (1 + (Math.random() * 0.2 - 0.1)))
      };
    } else if (after) {
      // Use the next known point with slight variation
      return {
        symbol,
        date: date.toISOString().split('T')[0],
        open: after.open * (1 + (Math.random() * 0.01 - 0.005)),
        high: after.high * (1 + (Math.random() * 0.01)),
        low: after.low * (1 - (Math.random() * 0.01)),
        close: after.close * (1 + (Math.random() * 0.01 - 0.005)),
        volume: Math.floor(after.volume * (1 + (Math.random() * 0.2 - 0.1)))
      };
    } else {
      // No reference points at all, create reasonable default
      return {
        symbol,
        date: date.toISOString().split('T')[0],
        open: 100 + Math.random() * 10,
        high: 105 + Math.random() * 10,
        low: 95 + Math.random() * 10,
        close: 100 + Math.random() * 10,
        volume: Math.floor(Math.random() * 1000000) + 500000
      };
    }
  }
  
  /**
   * Validate if data has sufficient continuity
   */
  private validateDataContinuity(
    data: HistoricalPrice[],
    startDate: string,
    endDate: string
  ): boolean {
    // Get all unique symbols
    const symbols = [...new Set(data.map(d => d.symbol))];
    
    // Get all unique dates
    const tradingDaysSet = new Set<string>();
    data.forEach(d => {
      const datePart = d.date.split('T')[0];
      tradingDaysSet.add(datePart);
    });
    
    // Check date coverage
    const start = new Date(startDate);
    const end = new Date(endDate);
    const tradingDays = Array.from(tradingDaysSet).sort();
    
    // Calculate expected number of trading days (excluding weekends)
    let expectedDays = 0;
    let current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) { // Skip weekends
        expectedDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    // Check if we have at least 90% of the expected trading days
    const coverage = tradingDays.length / expectedDays;
    
    // Check if each symbol has data for at least 90% of the trading days
    const symbolCoverage = symbols.map(symbol => {
      const symbolDates = new Set<string>();
      data.filter(d => d.symbol === symbol).forEach(d => {
        symbolDates.add(d.date.split('T')[0]);
      });
      return symbolDates.size / tradingDays.length;
    });
    
    const sufficientSymbolCoverage = symbolCoverage.every(c => c >= 0.9);
    
    return coverage >= 0.9 && sufficientSymbolCoverage;
  }
  
  /**
   * Check if data is sufficient for backtesting
   */
  private isDataSufficient(
    data: HistoricalPrice[],
    symbols: string[],
    startDate: string,
    endDate: string
  ): boolean {
    if (data.length === 0) return false;
    
    // Check if all symbols have data
    const dataSymbols = new Set(data.map(d => d.symbol));
    const allSymbolsHaveData = symbols.every(s => dataSymbols.has(s));
    
    // Check data continuity
    const isContinuous = this.validateDataContinuity(data, startDate, endDate);
    
    return allSymbolsHaveData && isContinuous;
  }
  
  /**
   * Merge real and mock data, prioritizing real data
   */
  private mergeRealAndMockData(
    realData: HistoricalPrice[],
    mockData: HistoricalPrice[]
  ): HistoricalPrice[] {
    // Create a map of existing real data points
    const realDataMap = new Map<string, HistoricalPrice>();
    
    realData.forEach(point => {
      const key = `${point.symbol}_${point.date.split('T')[0]}`;
      realDataMap.set(key, point);
    });
    
    // Filter mock data to only include points not in real data
    const filteredMockData = mockData.filter(point => {
      const key = `${point.symbol}_${point.date.split('T')[0]}`;
      return !realDataMap.has(key);
    });
    
    // Combine real and filtered mock data
    return [...realData, ...filteredMockData];
  }
  
  /**
   * Generate mock historical data as a fallback when API fails
   */
  public generateMockHistoricalData(
    symbols: string[],
    startDate: string,
    endDate: string,
    timeframe: string
  ): HistoricalPrice[] {
    const mockData: HistoricalPrice[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Determine base prices and trends for commonly traded stocks
    const getBasePrice = (symbol: string): number => {
      const symbolMap: Record<string, number> = {
        'AAPL': 170,
        'MSFT': 350,
        'GOOGL': 140,
        'AMZN': 170,
        'META': 400,
        'TSLA': 180,
        'NVDA': 800,
        'AMD': 150,
        'INTC': 30,
        'NFLX': 600,
        'ARM': 130,
        'SPY': 510,
        'QQQ': 420,
        'IWM': 205,
        'DIA': 380
      };
      
      return symbolMap[symbol] || 100 + Math.random() * 200; // Default to random value between 100-300
    };
    
    // Generate trend bias for each symbol (bullish, bearish, or sideways)
    const symbolTrends: Record<string, { bias: number, volatility: number }> = {};
    symbols.forEach(symbol => {
      // Bias: -0.5 to 0.5 (negative = bearish, positive = bullish)
      const bias = (Math.random() - 0.5) * 0.5; 
      
      // Volatility: 0.5% to 3% daily movement
      const volatility = Math.random() * 0.025 + 0.005;
      
      symbolTrends[symbol] = { bias, volatility };
    });
    
    // Create a date generator based on timeframe
    const getDates = (): Date[] => {
      const dates: Date[] = [];
      let current = new Date(start);
      
      while (current <= end) {
        // Skip weekends for daily data
        if (timeframe === 'day' || timeframe === 'week') {
          const day = current.getDay();
          if (day !== 0 && day !== 6) {
            dates.push(new Date(current));
          }
        } else {
          dates.push(new Date(current));
        }
        
        // Increment based on timeframe
        switch (timeframe) {
          case 'minute':
            current = new Date(current.getTime() + 60 * 1000);
            break;
          case '5minute':
            current = new Date(current.getTime() + 5 * 60 * 1000);
            break;
          case '15minute':
            current = new Date(current.getTime() + 15 * 60 * 1000);
            break;
          case 'hour':
            current = new Date(current.getTime() + 60 * 60 * 1000);
            break;
          case 'day':
            current = new Date(current.setDate(current.getDate() + 1));
            break;
          case 'week':
            current = new Date(current.setDate(current.getDate() + 7));
            break;
          default:
            current = new Date(current.setDate(current.getDate() + 1));
        }
      }
      
      return dates;
    };
    
    // Create a function to simulate market cycle patterns
    const simulateMarketCycles = (
      dates: Date[], 
      basePrice: number, 
      bias: number, 
      volatility: number
    ): Array<{ date: Date, price: number }> => {
      const prices: Array<{ date: Date, price: number }> = [];
      let currentPrice = basePrice;
      
      // Add some cycle patterns to create more realistic price movements
      const numCycles = Math.floor(Math.random() * 3) + 1; // 1-3 cycles in the period
      const cycleLength = dates.length / numCycles;
      const cycleAmplitude = basePrice * (Math.random() * 0.15 + 0.05); // 5-20% cycle amplitude
      
      dates.forEach((date, index) => {
        // Baseline trend from bias
        const trendComponent = bias * basePrice * (index / dates.length);
        
        // Cycle component (sine wave)
        const cyclePosition = (index % cycleLength) / cycleLength;
        const cycleComponent = Math.sin(cyclePosition * Math.PI * 2) * cycleAmplitude;
        
        // Random noise component based on volatility
        const randomComponent = (Math.random() * 2 - 1) * volatility * basePrice;
        
        // Combined price
        currentPrice = basePrice + trendComponent + cycleComponent + randomComponent;
        
        // Ensure price doesn't go negative
        currentPrice = Math.max(currentPrice, basePrice * 0.1);
        
        prices.push({ date, price: currentPrice });
      });
      
      return prices;
    };
    
    // Generate dates
    const dates = getDates();
    
    // Generate realistic price data for each symbol
    for (const symbol of symbols) {
      const basePrice = getBasePrice(symbol);
      const { bias, volatility } = symbolTrends[symbol];
      
      // Generate price series with market cycles
      const priceSimulation = simulateMarketCycles(dates, basePrice, bias, volatility);
      
      // Convert price simulation to historical data format
      priceSimulation.forEach(({ date, price }) => {
        // Generate realistic OHLC data around the simulated price
        const volatilityFactor = price * volatility;
        const high = price + (Math.random() * volatilityFactor);
        const low = price - (Math.random() * volatilityFactor);
        const open = low + Math.random() * (high - low);
        const close = price; // Use the simulated price as close
        
        // Volume tends to be higher on trend days
        const trendStrength = Math.abs(open - close) / volatilityFactor;
        const baseVolume = Math.floor(Math.random() * 1000000) + 100000;
        const volume = Math.floor(baseVolume * (1 + trendStrength));
        
        mockData.push({
          symbol,
          date: date.toISOString().split('T')[0],
          open,
          high: Math.max(high, open, close),
          low: Math.min(low, open, close),
          close,
          volume
        });
      });
    }
    
    return mockData;
  }

  /**
   * Generate synthetic future data based on historical data
   */
  private generateFutureData(
    historicalData: HistoricalPrice[],
    startDate: string,
    endDate: string,
    timeframe: string
  ): HistoricalPrice[] {
    if (historicalData.length === 0) {
      return [];
    }
    
    console.log(`Generating future data from ${startDate} to ${endDate}`);
    
    // Group data by symbol
    const symbolData: { [key: string]: HistoricalPrice[] } = {};
    historicalData.forEach(dataPoint => {
      if (!symbolData[dataPoint.symbol]) {
        symbolData[dataPoint.symbol] = [];
      }
      symbolData[dataPoint.symbol].push(dataPoint);
    });
    
    // Generate future dates
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const futureDates = this.generateDateRange(startDate, endDate, timeframe);
    
    // Generate future data for each symbol
    const futureData: HistoricalPrice[] = [];
    
    Object.keys(symbolData).forEach(symbol => {
      const symbolHistoricalData = symbolData[symbol];
      
      // Sort by date ascending
      symbolHistoricalData.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      
      // Get last 30 days of price movement to model future movement
      const lastNPrices = symbolHistoricalData.slice(-30);
      if (lastNPrices.length < 5) {
        // Not enough data to model future prices
        return;
      }
      
      // Calculate average daily price movement as a percentage
      const dailyChanges: number[] = [];
      for (let i = 1; i < lastNPrices.length; i++) {
        const prevClose = lastNPrices[i-1].close;
        const currentClose = lastNPrices[i].close;
        const percentChange = (currentClose - prevClose) / prevClose;
        dailyChanges.push(percentChange);
      }
      
      // Calculate statistics
      const avgDailyChange = dailyChanges.reduce((a, b) => a + b, 0) / dailyChanges.length;
      const stdDevDailyChange = Math.sqrt(
        dailyChanges.map(x => Math.pow(x - avgDailyChange, 2)).reduce((a, b) => a + b, 0) / dailyChanges.length
      );
      
      // Get latest price as starting point
      let currentPrice = lastNPrices[lastNPrices.length - 1].close;
      let currentHigh = currentPrice * 1.01;  // 1% above close
      let currentLow = currentPrice * 0.99;   // 1% below close
      let currentOpen = currentPrice;
      let currentVolume = lastNPrices[lastNPrices.length - 1].volume;
      
      // Generate future data points
      futureDates.forEach(date => {
        // Only generate if date is after startDate
        if (date <= startDateObj) {
          return;
        }
        
        // Model future price with random walk based on historical volatility
        const randomChange = avgDailyChange + (Math.random() * 2 - 1) * stdDevDailyChange;
        currentPrice = currentPrice * (1 + randomChange);
        
        // Generate intraday volatility for high/low
        const dailyVolatility = Math.max(0.005, stdDevDailyChange / 2);  // At least 0.5%
        currentHigh = currentPrice * (1 + dailyVolatility * (0.5 + Math.random()));
        currentLow = currentPrice * (1 - dailyVolatility * (0.5 + Math.random()));
        
        // Ensure high is highest and low is lowest
        if (currentHigh < currentPrice) currentHigh = currentPrice * 1.005;
        if (currentLow > currentPrice) currentLow = currentPrice * 0.995;
        
        // Open is previous close with small adjustment
        currentOpen = currentPrice * (1 + (Math.random() * 0.01 - 0.005));
        
        // Volume fluctuates but stays in similar range
        currentVolume = currentVolume * (0.8 + Math.random() * 0.4);
        
        // Create data point
        futureData.push({
          symbol,
          date: date.toISOString().split('T')[0],
          open: currentOpen,
          high: currentHigh,
          low: currentLow,
          close: currentPrice,
          volume: Math.round(currentVolume),
          // Add marker that this is synthetic future data
          isSynthetic: true,
          isFuture: true
        });
      });
    });
    
    console.log(`Generated ${futureData.length} future data points`);
    return futureData;
  }
}